import { useEffect, useState } from "react";
import {
  X, Loader2, Award, Zap, Share2, Gift, Sparkles, Mail, TrendingUp, Lock,
  Twitter, Facebook, Linkedin, MessageCircle,
} from "lucide-react";
import { adsApiCall } from "./api";
import { toast } from "sonner";

const ICON_MAP = { Award, Zap, Share2, Gift, Sparkles, Mail, TrendingUp };
const TIER_COLORS = {
  bronze:   { bg: "rgba(205, 127, 50, 0.14)",  border: "rgba(205, 127, 50, 0.45)",  fg: "#cd7f32" },
  silver:   { bg: "rgba(192, 192, 192, 0.14)", border: "rgba(192, 192, 192, 0.5)",  fg: "#c0c0c0" },
  gold:     { bg: "rgba(255, 200, 0, 0.18)",   border: "rgba(255, 200, 0, 0.55)",   fg: "#ffca28" },
  platinum: { bg: "rgba(124, 58, 237, 0.16)",  border: "rgba(124, 58, 237, 0.55)",  fg: "#b48bff" },
};

export default function Achievements({ onClose }) {
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(null); // badge being shared
  useEffect(() => {
    adsApiCall.achievementsMine()
      .then(setData)
      .catch(() => { toast.error("Couldn't load achievements"); onClose(); });
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cp-modal-backdrop" onClick={onClose} data-testid="achievements-modal">
      <div className="cp-modal ach-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ads-mobile-sheet-close" style={{ display: "flex" }} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className="cp-modal-title">Achievements</div>
        {!data ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : (
          <>
            <div className="cp-modal-sub">
              <b>{data.unlocked}</b> of <b>{data.total}</b> unlocked — every milestone rewards you with bragging rights.
            </div>
            <div className="ach-grid" data-testid="achievements-grid">
              {data.badges.map((b) => {
                const Icon = ICON_MAP[b.icon] || Award;
                const colors = TIER_COLORS[b.tier] || TIER_COLORS.bronze;
                const pct = Math.min(100, Math.round((b.progress / b.target) * 100));
                return (
                  <div
                    key={b.id}
                    className={`ach-card ${b.unlocked ? "unlocked" : "locked"}`}
                    data-testid={`ach-${b.id}`}
                    style={{ "--tier-bg": colors.bg, "--tier-border": colors.border, "--tier-fg": colors.fg }}
                  >
                    <div className="ach-icon">
                      {b.unlocked ? <Icon size={22} /> : <Lock size={18} />}
                    </div>
                    <div className="ach-tier">{b.tier}</div>
                    <div className="ach-label">{b.label}</div>
                    <div className="ach-desc">{b.description}</div>
                    <div className="ach-progress-row">
                      <div className="ach-progress-bar">
                        <div className="ach-progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="ach-progress-text">
                        {b.progress}/{b.target}
                      </div>
                    </div>
                    {b.unlocked && data.email_hash && (
                      <button
                        className="ads-btn ads-btn-ghost ach-share"
                        onClick={() => setSharing({ ...b, emailHash: data.email_hash })}
                        data-testid={`ach-share-${b.id}`}
                      >
                        <Share2 size={11} /> Share
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {sharing && <ShareAchievementModal badge={sharing} onClose={() => setSharing(null)} />}
      </div>
    </div>
  );
}

function ShareAchievementModal({ badge, onClose }) {
  const url = `${window.location.origin}/a/${badge.emailHash}/${badge.id}`;
  const msg = `I just unlocked "${badge.label}" on TopRankPulse 🏆`;
  const encMsg = encodeURIComponent(`${msg} — ${url}`);
  const encUrl = encodeURIComponent(url);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch { toast.error("Couldn't copy"); }
  }

  const channels = [
    { id: "twitter",  label: "X / Twitter", icon: Twitter,   href: `https://twitter.com/intent/tweet?text=${encMsg}` },
    { id: "linkedin", label: "LinkedIn",    icon: Linkedin,  href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}` },
    { id: "facebook", label: "Facebook",    icon: Facebook,  href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}` },
    { id: "whatsapp", label: "WhatsApp",    icon: MessageCircle, href: `https://wa.me/?text=${encMsg}` },
  ];

  return (
    <div className="cp-modal-backdrop" onClick={onClose} data-testid="ach-share-modal">
      <div className="cp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <button className="ads-mobile-sheet-close" style={{ display: "flex" }} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className="cp-modal-title">Share your badge</div>
        <div className="cp-modal-sub">"{badge.label}" · {badge.tier.toUpperCase()}</div>
        <div className="cp-link-row">
          <input className="ads-prop-input" value={url} readOnly data-testid="ach-share-url" onFocus={(e) => e.target.select()} />
          <button className="ads-btn ads-btn-primary" onClick={copy} data-testid="ach-share-copy">Copy</button>
        </div>
        <div className="cp-social-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {channels.map((c) => (
            <a
              key={c.id}
              href={c.href}
              target="_blank"
              rel="noreferrer"
              className="cp-social-btn"
              data-testid={`ach-share-${c.id}`}
              style={{ "--ch": "#a855f7" }}
            >
              <c.icon size={16} />
              <span>{c.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
